/**
 * WebSocketProvider
 *
 * React context provider that initializes and manages the WebSocketService.
 * Connects to the solana-ws-proxy edge function (upstream: Triton Whirligig).
 *
 * WebSocket is always enabled — no feature flag.
 */

import { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { WebSocketService } from '@/services/ws';

interface WebSocketContextValue {
  service: WebSocketService;
  enabled: boolean;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';

function buildWsProxyUrl(): string {
  if (!SUPABASE_URL) return '';
  // Convert https://xxx.supabase.co to wss://xxx.supabase.co
  const wsBase = SUPABASE_URL.replace(/^https?:\/\//, 'wss://');
  // Append apikey for Supabase gateway auth
  return `${wsBase}/functions/v1/solana-ws-proxy?apikey=${SUPABASE_ANON_KEY}`;
}

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const service = useMemo(() => WebSocketService.getInstance(), []);

  useEffect(() => {
    const wsUrl = buildWsProxyUrl();
    if (!wsUrl) {
      console.warn('[WSProvider] Cannot build WebSocket proxy URL');
      return;
    }

    service.connect(wsUrl);

    return () => {
      service.disconnect();
    };
  }, [service]);

  const value = useMemo<WebSocketContextValue>(
    () => ({ service, enabled: true }),
    [service],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Access the WebSocketService from React components.
 * Returns null if the provider is not mounted (safe to use optionally).
 */
export function useWebSocket(): WebSocketContextValue | null {
  return useContext(WebSocketContext);
}
